import { inject, injectable } from 'inversify'
import type { RouteLocationNamedRaw } from 'vue-router'
import { SERVICE_TYPES } from '@/core/container/types'
import type { Resolvable } from '@/services/resolvers/types'
import type { RouterPermissionInterface } from '@/services/types'
import { gettingStartedRoutName } from '@/routes/user/getting-started/types'
import TmLogicError from '@/core/error/tmLogicError'
import type { TmNamedRoute } from '@/services/route/types'
import { ScheduledRoutes } from '@/routes/user/scheduled/types'
import { MessengerRouteNames } from '@/routes/user/messenger/types'
import type SettingsSectionRedirectResolver from '@/services/resolvers/settingsSectionRedirectResolver'
import { templateRouteNames } from '@/routes/user/templates'
import { UserSettingsRoutes } from '@/routes/user/settings'
import {
  contactsBlockedRoute,
  contactsImportsRoute,
  contactsListsFoldersRoute,
  contactsListsRoute,
  contactsNotesRoute,
  contactsSegmentsRoute,
  contactsUnsubscribedRoute,
} from '@/routes/user/contacts/contacts'
import { contactsMyRoute } from '@/routes/user/contacts/contactsMy'
import type ReportingRedirectResolver from '@/services/resolvers/ReportingRedirectResolver'

export type MainSidebarRouteInfo =
  | string
  | string[]
  | {
      routeName: string
      childrenRoutesList: Readonly<
        {
          name: string
          route: TmNamedRoute | RouteLocationNamedRaw
          childrenRoutesList?: Readonly<
            {
              name: string
              route: TmNamedRoute | RouteLocationNamedRaw
            }[]
          >
        }[]
      >
      navigateToParentRoute?: boolean
    }

type RoutesMapKeys =
  | 'GettingStartedMenuItem'
  | 'ComposeMainSidebarItem'
  | 'MessengerMainSidebarItem'
  | 'ContactsMainSidebarItem'
  | 'ScheduledMainSidebarItem'
  | 'TemplatesMainSidebarItem'
  | 'ReportingMainSidebarItem'
  | 'SettingsMainSidebarItem'
@injectable()
export default class MainSidebarRoutesResolverService implements Resolvable {
  constructor(
    @inject(SERVICE_TYPES.RouterPermissionsService)
    private readonly routerPermissionsService: RouterPermissionInterface,
    @inject(SERVICE_TYPES.SettingsSectionRedirectResolver)
    private readonly settingsSectionRedirectResolver: SettingsSectionRedirectResolver,
    @inject(SERVICE_TYPES.ReportingRedirectResolver)
    private readonly reportingRedirectResolver: ReportingRedirectResolver,
  ) {}

  public getContactsSectionRoutes() {
    return [
      {
        name: 'contact',
        route: { name: contactsMyRoute.name },
      },
      {
        name: 'list',
        route: { name: contactsListsRoute.name },
      },
      {
        name: 'listFolders',
        route: { name: contactsListsFoldersRoute.name },
      },
      {
        name: 'filteredView',
        route: { name: contactsSegmentsRoute.name },
      },
      {
        name: 'tag',
        route: { name: 'user.contacts.tags' },
      },
      {
        name: 'note',
        route: { name: contactsNotesRoute.name },
      },
      {
        name: 'import',
        route: { name: contactsImportsRoute.name },
      },
      {
        name: 'unsubscriber',
        route: { name: contactsUnsubscribedRoute.name },
      },
      {
        name: 'blockedContact',
        route: { name: contactsBlockedRoute.name },
      },
    ]
  }

  public getScheduledSectionRoutes() {
    return [
      {
        name: 'upcoming',
        route: { name: ScheduledRoutes.upcoming },
      },
      {
        name: 'paused',
        route: { name: ScheduledRoutes.paused },
      },
      {
        name: 'notSent',
        route: { name: ScheduledRoutes.notSent },
      },
      {
        name: 'completed',
        route: { name: ScheduledRoutes.completed },
      },
      {
        name: 'calendar',
        route: { name: ScheduledRoutes.calendar },
      },
    ] as const
  }

  public getServicesSectionRoutes() {
    return [
      {
        name: 'byoc',
        route: { name: UserSettingsRoutes.SmsVoiceByocIndex },
      },
    ] as const
  }

  public getReportSectionRoutes() {
    return this.reportingRedirectResolver.getReportSectionRoutes()
  }

  public getRoutesMapStructure(): Record<RoutesMapKeys, MainSidebarRouteInfo> {
    return {
      GettingStartedMenuItem: gettingStartedRoutName,
      ComposeMainSidebarItem: 'user.compose',
      MessengerMainSidebarItem: MessengerRouteNames.root,
      ContactsMainSidebarItem: {
        routeName: 'user.contacts',
        childrenRoutesList: this.getContactsSectionRoutes(),
        navigateToParentRoute: true,
      },
      ScheduledMainSidebarItem: {
        routeName: ScheduledRoutes.scheduled,
        childrenRoutesList: this.getScheduledSectionRoutes(),
      },
      TemplatesMainSidebarItem: templateRouteNames.root,
      ReportingMainSidebarItem: {
        routeName: 'user.reporting',
        childrenRoutesList: this.getReportSectionRoutes(),
      },
      SettingsMainSidebarItem: {
        routeName: 'user.settings',
        childrenRoutesList: this.settingsSectionRedirectResolver
          .getRoutesGroups()
          .map((menuGroup) => menuGroup.links)
          .flat()
          .map((menuLink) => {
            return {
              name: menuLink.route.name,
              route: menuLink.route,
            }
          }),
      },
    } as const
  }

  public getMainRoutesMap() {
    const routesMap = this.getRoutesMapStructure()
    const preparedRoutes: Partial<Record<keyof typeof routesMap, string | string[] | undefined>> = {}
    for (const _key in routesMap) {
      const key = _key as keyof typeof routesMap
      const routeInfo = routesMap[key]
      if (typeof routeInfo === 'string') {
        preparedRoutes[key] = routeInfo
      } else if (Array.isArray(routeInfo)) {
        preparedRoutes[key] = routeInfo
      } else {
        preparedRoutes[key] = routeInfo.routeName
      }
    }
    return preparedRoutes
  }

  public getRoutesMap() {
    const routesMap = this.getRoutesMapStructure()
    const preparedRoutes: Partial<Record<keyof typeof routesMap, string | undefined>> = {}
    for (const _key in routesMap) {
      const key = _key as keyof typeof routesMap
      const routeInfo = routesMap[key]
      if (typeof routeInfo === 'string') {
        preparedRoutes[key] = routeInfo
      } else if (Array.isArray(routeInfo)) {
        const firstAllowedRoute = routeInfo.find((t) => {
          return this.routerPermissionsService.isAllowAccessToRoute({
            name: t,
          })
        })
        preparedRoutes[key] = firstAllowedRoute
      } else if (!routeInfo.childrenRoutesList || routeInfo.childrenRoutesList.length === 0) {
        preparedRoutes[key] = routeInfo.routeName
      } else {
        const firstAllowedChildrenRoute = routeInfo.childrenRoutesList.find((t) => {
          return this.routerPermissionsService.isAllowAccessToRoute(t.route)
        })
        if (!firstAllowedChildrenRoute) {
          continue
        }
        const firstAllowedChildrenRouteName = firstAllowedChildrenRoute.route.name
        if (typeof firstAllowedChildrenRouteName !== 'string') {
          throw new TmLogicError(
            `firstAllowedChildrenRoute is typeof "${typeof firstAllowedChildrenRouteName}", expected string`,
          )
        }
        preparedRoutes[key] = routeInfo.navigateToParentRoute ? routeInfo.routeName : firstAllowedChildrenRouteName
      }
    }

    return preparedRoutes
  }

  public async resolve() {
    const routes = Object.values(this.getRoutesMapStructure())
    await Promise.all(
      routes.map((t) => {
        if (typeof t === 'string') {
          return this.routerPermissionsService.resolveStrategiesForRoute(t)
        }

        if (Array.isArray(t)) {
          return Promise.all(
            t.map((child) => {
              return this.routerPermissionsService.resolveStrategiesForRoute(child)
            }),
          )
        }

        const promisesList: Promise<unknown>[] = [this.routerPermissionsService.resolveStrategiesForRoute(t.routeName)]
        t.childrenRoutesList.forEach((child: (typeof t.childrenRoutesList)[number]) => {
          promisesList.push(this.routerPermissionsService.resolveStrategiesForRoute(child.route))
          if (child.childrenRoutesList) {
            child.childrenRoutesList.forEach((r) => {
              promisesList.push(this.routerPermissionsService.resolveStrategiesForRoute(r.route))
            })
          }
        })

        return Promise.all(promisesList)
      }),
    )
  }
}
